import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Sparkles,
    Users,
    Key,
    Bot,
    ArrowRight,
    X,
    ChevronLeft,
    Check
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";

const slides = [
    {
        id: "ai",
        title: "Meet Ask HomeEzi",
        description: "Your new personal home assistant. Ask about your paint codes, appliance warranties, and get instant answers about your home.",
        icon: Bot,
        color: "text-indigo-500",
        bg: "bg-indigo-500/10"
    },
    {
        id: "directory",
        title: "Local Directory",
        description: "Find trusted local providers. See real reviews and find verified electricians, plumbers, and more in your area.",
        icon: Users,
        color: "text-purple-500",
        bg: "bg-purple-500/10"
    },
    {
        id: "info",
        title: "House Secrets",
        description: "Never forget a gate code again. Store WiFi passwords, alarm codes, and water shut-off locations directly in your Home Records.",
        icon: Key,
        color: "text-amber-500",
        bg: "bg-amber-500/10"
    }
];

const NewFeaturesGuide = ({ open, onOpenChange }) => {
    const { user } = useAuth();
    if (!user) return null;
    const [currentIndex, setCurrentIndex] = useState(0);

    const handleNext = () => {
        if (currentIndex < slides.length - 1) {
            setCurrentIndex(prev => prev + 1);
        } else {
            handleComplete();
        }
    };



    const handleComplete = () => {
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md p-0 overflow-hidden bg-white/95 backdrop-blur-3xl border-none shadow-2xl rounded-[2.5rem] gap-0">
                {/* Header Badge */}
                <div className="absolute top-6 left-1/2 -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full shadow-lg z-20">
                    <div className="flex items-center gap-1.5">
                        <Sparkles className="w-3 h-3 text-white" />
                        <span className="text-[10px] font-bold text-white uppercase tracking-wider">What's New</span>
                    </div>
                </div>

                {/* Close Button */}
                <div className="absolute top-4 right-4 z-50">
                    <button
                        onClick={handleComplete}
                        className="p-2 rounded-full hover:bg-black/5 transition-colors text-muted-foreground"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Main Content Area */}
                <div className="relative h-[500px] w-full flex flex-col justify-between p-8 pt-16">

                    {/* Slides Container */}
                    <div className="flex-1 relative mt-4">
                        <AnimatePresence initial={false} mode="wait" custom={currentIndex}>
                            <motion.div
                                key={currentIndex}
                                custom={currentIndex}
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                                className="absolute inset-0 flex flex-col items-center text-center"
                            >
                                {/* Icon Bubble */}
                                <div className={cn(
                                    "w-32 h-32 rounded-[2rem] flex items-center justify-center mb-8 shadow-xl shadow-gray-100 transform transition-transform hover:scale-105",
                                    slides[currentIndex].bg
                                )}>
                                    {(() => {
                                        const Icon = slides[currentIndex].icon;
                                        return <Icon className={cn("w-14 h-14", slides[currentIndex].color)} />;
                                    })()}
                                </div>

                                <h2 className="text-2xl font-bold tracking-tight mb-4 text-foreground">
                                    {slides[currentIndex].title}
                                </h2>

                                <p className="text-muted-foreground leading-relaxed px-4 text-sm font-medium">
                                    {slides[currentIndex].description}
                                </p>
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    {/* Footer Controls */}
                    <div className="flex flex-col gap-6 mt-8 z-10">
                        {/* Dots Indicator */}
                        <div className="flex justify-center gap-2">
                            {slides.map((_, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => setCurrentIndex(idx)}
                                    className={cn(
                                        "h-1.5 rounded-full transition-all duration-300",
                                        idx === currentIndex ? "w-6 bg-primary" : "w-1.5 bg-gray-200"
                                    )}
                                />
                            ))}
                        </div>

                        {/* Navigation Buttons */}
                        <div className="flex items-center gap-3">
                            <Button
                                className="flex-1 h-12 rounded-2xl text-base font-bold shadow-lg shadow-primary/20 hover:shadow-primary/30 active:scale-95 transition-all bg-gradient-to-r from-primary to-primary/90"
                                onClick={handleNext}
                            >
                                {currentIndex === slides.length - 1 ? (
                                    <>
                                        Awesome, Let's Go! <Check className="w-5 h-5 ml-2" />
                                    </>
                                ) : (
                                    <>
                                        Next Feature <ArrowRight className="w-5 h-5 ml-2" />
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default NewFeaturesGuide;
